import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  LayoutDashboard,
  Save,
  Plus,
  Trash2,
  Edit,
  Eye,
  Upload,
  Video,
  Image,
  GripVertical,
  Building2,
  Users,
  TrendingUp,
  Quote,
  Settings,
  ExternalLink,
  RefreshCw,
  CheckCircle,
  Star,
  FileText,
  Layers
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available icons for selection
const ICON_OPTIONS = [
  "Building2", "Users", "TrendingUp", "Star", "CheckCircle", "FileText",
  "Shield", "Clock", "DollarSign", "Award", "Target", "Zap", "Heart",
  "ThumbsUp", "Briefcase", "HardHat", "Wrench", "Truck", "MapPin", "Phone"
];

// Available colors
const COLOR_OPTIONS = [
  { value: "blue", label: "Blå", class: "bg-blue-500" },
  { value: "green", label: "Grønn", class: "bg-green-500" },
  { value: "purple", label: "Lilla", class: "bg-purple-500" },
  { value: "orange", label: "Oransje", class: "bg-orange-500" },
  { value: "cyan", label: "Cyan", class: "bg-cyan-500" },
  { value: "pink", label: "Rosa", class: "bg-pink-500" },
  { value: "yellow", label: "Gul", class: "bg-yellow-500" },
  { value: "red", label: "Rød", class: "bg-red-500" }
];

export default function AdminLandingPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [activeTab, setActiveTab] = useState("hero");
  
  // Modal states
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showStatModal, setShowStatModal] = useState(false);
  const [showTestimonialModal, setShowTestimonialModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Form states
  const [heroForm, setHeroForm] = useState({
    title_en: "", title_no: "",
    subtitle_en: "", subtitle_no: "",
    primary_button_text_en: "", primary_button_text_no: "", primary_button_link: "",
    secondary_button_text_en: "", secondary_button_text_no: "", secondary_button_link: ""
  });
  
  const [serviceForm, setServiceForm] = useState({
    title: "", title_no: "",
    description: "", description_no: "",
    icon: "Building2", color: "blue",
    link: "", is_coming_soon: false
  });
  
  const [videoForm, setVideoForm] = useState({
    title: "", title_no: "",
    subtitle: "", subtitle_no: "",
    button_text: "", button_text_no: "",
    button_link: "", color: "purple", icon: "Building2",
    videoFile: null, posterFile: null
  });
  
  const [statForm, setStatForm] = useState({
    value: "", label: "", label_no: "", icon: "TrendingUp"
  });
  
  const [testimonialForm, setTestimonialForm] = useState({
    quote: "", quote_no: "",
    author_name: "", author_role: "", author_role_no: "",
    author_company: "", rating: 5
  });
  
  const [footerForm, setFooterForm] = useState({
    company_name: "", org_number: "",
    address: "", email: "", phone: "",
    facebook: "", instagram: "", linkedin: ""
  });

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/landing-cms/admin/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        
        // Populate hero form
        if (data.hero) {
          setHeroForm({
            title_en: data.hero.title_en || "",
            title_no: data.hero.title_no || "",
            subtitle_en: data.hero.subtitle_en || "",
            subtitle_no: data.hero.subtitle_no || "",
            primary_button_text_en: data.hero.primary_button?.text_en || "",
            primary_button_text_no: data.hero.primary_button?.text_no || "",
            primary_button_link: data.hero.primary_button?.link || "",
            secondary_button_text_en: data.hero.secondary_button?.text_en || "",
            secondary_button_text_no: data.hero.secondary_button?.text_no || "",
            secondary_button_link: data.hero.secondary_button?.link || ""
          });
        }
        
        // Populate footer form
        if (data.footer) {
          setFooterForm({
            company_name: data.footer.company_name || "",
            org_number: data.footer.org_number || "",
            address: data.footer.address || "",
            email: data.footer.email || "",
            phone: data.footer.phone || "",
            facebook: data.footer.social_links?.facebook || "",
            instagram: data.footer.social_links?.instagram || "",
            linkedin: data.footer.social_links?.linkedin || ""
          });
        }
      }
    } catch (error) {
      console.error("Error fetching config:", error);
      toast.error("Kunne ikke laste konfigurasjon");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // ============ HERO SECTION ============
  const saveHero = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(heroForm).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const res = await fetch(`${API_URL}/api/landing-cms/hero`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) throw new Error("Kunne ikke lagre");
      toast.success("Hero-seksjon oppdatert!");
      fetchConfig();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadHeroMedia = async (file, type) => {
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(heroForm).forEach(([key, value]) => {
        formData.append(key, value);
      });
      
      if (type === "video") {
        formData.append("background_video", file);
      } else {
        formData.append("background_image", file);
      }

      const res = await fetch(`${API_URL}/api/landing-cms/hero`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) throw new Error("Opplasting feilet");
      toast.success(`${type === "video" ? "Video" : "Bilde"} lastet opp!`);
      fetchConfig();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  // ============ SERVICES ============
  const saveService = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(serviceForm).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const url = editingItem 
        ? `${API_URL}/api/landing-cms/services/${editingItem.card_id}`
        : `${API_URL}/api/landing-cms/services`;

      const res = await fetch(url, {
        method: editingItem ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) throw new Error("Kunne ikke lagre");
      toast.success(editingItem ? "Tjeneste oppdatert!" : "Tjeneste lagt til!");
      setShowServiceModal(false);
      setEditingItem(null);
      resetServiceForm();
      fetchConfig();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteService = async (cardId) => {
    if (!confirm("Er du sikker på at du vil slette denne tjenesten?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/landing-cms/services/${cardId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Kunne ikke slette");
      toast.success("Tjeneste slettet!");
      fetchConfig();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetServiceForm = () => {
    setServiceForm({
      title: "", title_no: "",
      description: "", description_no: "",
      icon: "Building2", color: "blue",
      link: "", is_coming_soon: false
    });
  };

  // ============ VIDEOS ============
  const saveVideo = async () => {
    if (!videoForm.videoFile && !editingItem) {
      toast.error("Velg en videofil");
      return;
    }
    
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("title", videoForm.title);
      formData.append("title_no", videoForm.title_no);
      formData.append("subtitle", videoForm.subtitle);
      formData.append("subtitle_no", videoForm.subtitle_no);
      formData.append("button_text", videoForm.button_text);
      formData.append("button_text_no", videoForm.button_text_no);
      formData.append("button_link", videoForm.button_link);
      formData.append("color", videoForm.color);
      formData.append("icon", videoForm.icon);
      
      if (videoForm.videoFile) {
        formData.append("video", videoForm.videoFile);
      }
      if (videoForm.posterFile) {
        formData.append("poster", videoForm.posterFile);
      }

      const url = editingItem 
        ? `${API_URL}/api/landing-cms/videos/${editingItem.video_id}`
        : `${API_URL}/api/landing-cms/videos`;

      const res = await fetch(url, {
        method: editingItem ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) throw new Error("Kunne ikke lagre");
      toast.success(editingItem ? "Video oppdatert!" : "Video lastet opp!");
      setShowVideoModal(false);
      setEditingItem(null);
      resetVideoForm();
      fetchConfig();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteVideo = async (videoId) => {
    if (!confirm("Er du sikker på at du vil slette denne videoen?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/landing-cms/videos/${videoId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Kunne ikke slette");
      toast.success("Video slettet!");
      fetchConfig();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetVideoForm = () => {
    setVideoForm({
      title: "", title_no: "",
      subtitle: "", subtitle_no: "",
      button_text: "", button_text_no: "",
      button_link: "", color: "purple", icon: "Building2",
      videoFile: null, posterFile: null
    });
  };

  // ============ STATS ============
  const saveStat = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(statForm).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const url = editingItem 
        ? `${API_URL}/api/landing-cms/stats/${editingItem.stat_id}`
        : `${API_URL}/api/landing-cms/stats`;

      const res = await fetch(url, {
        method: editingItem ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) throw new Error("Kunne ikke lagre");
      toast.success(editingItem ? "Statistikk oppdatert!" : "Statistikk lagt til!");
      setShowStatModal(false);
      setEditingItem(null);
      setStatForm({ value: "", label: "", label_no: "", icon: "TrendingUp" });
      fetchConfig();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteStat = async (statId) => {
    if (!confirm("Er du sikker på at du vil slette denne statistikken?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/landing-cms/stats/${statId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Kunne ikke slette");
      toast.success("Statistikk slettet!");
      fetchConfig();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // ============ TESTIMONIALS ============
  const saveTestimonial = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(testimonialForm).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const url = editingItem 
        ? `${API_URL}/api/landing-cms/testimonials/${editingItem.testimonial_id}`
        : `${API_URL}/api/landing-cms/testimonials`;

      const res = await fetch(url, {
        method: editingItem ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) throw new Error("Kunne ikke lagre");
      toast.success(editingItem ? "Anmeldelse oppdatert!" : "Anmeldelse lagt til!");
      setShowTestimonialModal(false);
      setEditingItem(null);
      setTestimonialForm({
        quote: "", quote_no: "",
        author_name: "", author_role: "", author_role_no: "",
        author_company: "", rating: 5
      });
      fetchConfig();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteTestimonial = async (testimonialId) => {
    if (!confirm("Er du sikker på at du vil slette denne anmeldelsen?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/landing-cms/testimonials/${testimonialId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Kunne ikke slette");
      toast.success("Anmeldelse slettet!");
      fetchConfig();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // ============ FOOTER ============
  const saveFooter = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(footerForm).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const res = await fetch(`${API_URL}/api/landing-cms/footer`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) throw new Error("Kunne ikke lagre");
      toast.success("Footer oppdatert!");
      fetchConfig();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6" data-testid="admin-landing-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <LayoutDashboard className="w-6 h-6 text-[#23F7DD]" />
              Landing Page Editor
            </h1>
            <p className="text-gray-400 mt-1">
              Konfigurer innhold på hjemmesiden
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={fetchConfig}
              className="border-[#2a2a4a] text-gray-300"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Oppdater
            </Button>
            <a href="/" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-[#23F7DD] text-[#23F7DD]">
                <Eye className="w-4 h-4 mr-2" />
                Se live
                <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            </a>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#1a1a2e] border border-[#2a2a4a]">
            <TabsTrigger value="hero" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-black">
              Hero
            </TabsTrigger>
            <TabsTrigger value="services" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-black">
              Tjenester
            </TabsTrigger>
            <TabsTrigger value="videos" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-black">
              Videoer
            </TabsTrigger>
            <TabsTrigger value="stats" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-black">
              Statistikk
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-black">
              Anmeldelser
            </TabsTrigger>
            <TabsTrigger value="footer" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-black">
              Footer
            </TabsTrigger>
          </TabsList>

          {/* HERO TAB */}
          <TabsContent value="hero" className="space-y-6">
            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardHeader>
                <CardTitle className="text-white">Hero-seksjon</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Title */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Tittel (Engelsk)</Label>
                    <Input
                      value={heroForm.title_en}
                      onChange={(e) => setHeroForm({...heroForm, title_en: e.target.value})}
                      className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Tittel (Norsk)</Label>
                    <Input
                      value={heroForm.title_no}
                      onChange={(e) => setHeroForm({...heroForm, title_no: e.target.value})}
                      className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                    />
                  </div>
                </div>
                
                {/* Subtitle */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Undertittel (Engelsk)</Label>
                    <Textarea
                      value={heroForm.subtitle_en}
                      onChange={(e) => setHeroForm({...heroForm, subtitle_en: e.target.value})}
                      className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Undertittel (Norsk)</Label>
                    <Textarea
                      value={heroForm.subtitle_no}
                      onChange={(e) => setHeroForm({...heroForm, subtitle_no: e.target.value})}
                      className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                    />
                  </div>
                </div>

                {/* Primary Button */}
                <div className="border-t border-[#2a2a4a] pt-4">
                  <h4 className="text-white font-medium mb-3">Primærknapp</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-300">Tekst (EN)</Label>
                      <Input
                        value={heroForm.primary_button_text_en}
                        onChange={(e) => setHeroForm({...heroForm, primary_button_text_en: e.target.value})}
                        className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Tekst (NO)</Label>
                      <Input
                        value={heroForm.primary_button_text_no}
                        onChange={(e) => setHeroForm({...heroForm, primary_button_text_no: e.target.value})}
                        className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Link</Label>
                      <Input
                        value={heroForm.primary_button_link}
                        onChange={(e) => setHeroForm({...heroForm, primary_button_link: e.target.value})}
                        className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                        placeholder="/client/request-project"
                      />
                    </div>
                  </div>
                </div>

                {/* Secondary Button */}
                <div className="border-t border-[#2a2a4a] pt-4">
                  <h4 className="text-white font-medium mb-3">Sekundærknapp</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-300">Tekst (EN)</Label>
                      <Input
                        value={heroForm.secondary_button_text_en}
                        onChange={(e) => setHeroForm({...heroForm, secondary_button_text_en: e.target.value})}
                        className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Tekst (NO)</Label>
                      <Input
                        value={heroForm.secondary_button_text_no}
                        onChange={(e) => setHeroForm({...heroForm, secondary_button_text_no: e.target.value})}
                        className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Link</Label>
                      <Input
                        value={heroForm.secondary_button_link}
                        onChange={(e) => setHeroForm({...heroForm, secondary_button_link: e.target.value})}
                        className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                        placeholder="#services"
                      />
                    </div>
                  </div>
                </div>

                {/* Background Media */}
                <div className="border-t border-[#2a2a4a] pt-4">
                  <h4 className="text-white font-medium mb-3">Bakgrunnsmedier</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-[#0f0f1a] rounded-lg border border-dashed border-[#2a2a4a]">
                      <div className="flex items-center gap-2 mb-2">
                        <Video className="w-5 h-5 text-[#23F7DD]" />
                        <span className="text-white font-medium">Bakgrunnsvideo</span>
                      </div>
                      {config?.hero?.background_video && (
                        <p className="text-gray-400 text-sm mb-2 truncate">
                          Nåværende: {config.hero.background_video.split('/').pop()}
                        </p>
                      )}
                      <input
                        type="file"
                        accept="video/*"
                        onChange={(e) => e.target.files?.[0] && uploadHeroMedia(e.target.files[0], "video")}
                        className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#23F7DD] file:text-black hover:file:bg-[#1de9cf]"
                      />
                    </div>
                    <div className="p-4 bg-[#0f0f1a] rounded-lg border border-dashed border-[#2a2a4a]">
                      <div className="flex items-center gap-2 mb-2">
                        <Image className="w-5 h-5 text-purple-400" />
                        <span className="text-white font-medium">Bakgrunnsbilde (fallback)</span>
                      </div>
                      {config?.hero?.background_image && (
                        <p className="text-gray-400 text-sm mb-2 truncate">
                          Nåværende: {config.hero.background_image.split('/').pop()}
                        </p>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => e.target.files?.[0] && uploadHeroMedia(e.target.files[0], "image")}
                        className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-500 file:text-white hover:file:bg-purple-600"
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={saveHero} 
                  disabled={saving}
                  className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Lagrer..." : "Lagre endringer"}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SERVICES TAB */}
          <TabsContent value="services" className="space-y-6">
            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Tjenester</CardTitle>
                <Button 
                  onClick={() => { resetServiceForm(); setEditingItem(null); setShowServiceModal(true); }}
                  className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Legg til tjeneste
                </Button>
              </CardHeader>
              <CardContent>
                {config?.services?.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Layers className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Ingen tjenester lagt til ennå</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {config?.services?.sort((a, b) => a.order - b.order).map((service) => (
                      <div 
                        key={service.card_id}
                        className="flex items-center gap-4 p-4 bg-[#0f0f1a] rounded-lg border border-[#2a2a4a]"
                      >
                        <GripVertical className="w-5 h-5 text-gray-600 cursor-move" />
                        <div className={`w-10 h-10 rounded-lg bg-${service.color}-500/20 flex items-center justify-center`}>
                          <Building2 className={`w-5 h-5 text-${service.color}-400`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-white font-medium">{service.title}</h4>
                            {service.is_coming_soon && (
                              <Badge className="bg-yellow-500/20 text-yellow-400">Kommer snart</Badge>
                            )}
                          </div>
                          <p className="text-gray-400 text-sm line-clamp-1">{service.description}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => {
                              setEditingItem(service);
                              setServiceForm({
                                title: service.title,
                                title_no: service.title_no || "",
                                description: service.description,
                                description_no: service.description_no || "",
                                icon: service.icon,
                                color: service.color,
                                link: service.link || "",
                                is_coming_soon: service.is_coming_soon
                              });
                              setShowServiceModal(true);
                            }}
                            className="text-gray-400 hover:text-white"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => deleteService(service.card_id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* VIDEOS TAB */}
          <TabsContent value="videos" className="space-y-6">
            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Videoer</CardTitle>
                <Button 
                  onClick={() => { resetVideoForm(); setEditingItem(null); setShowVideoModal(true); }}
                  className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Last opp video
                </Button>
              </CardHeader>
              <CardContent>
                {config?.how_it_works?.videos?.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Video className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Ingen videoer lagt til ennå</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {config?.how_it_works?.videos?.map((video) => (
                      <div 
                        key={video.video_id}
                        className="p-4 bg-[#0f0f1a] rounded-lg border border-[#2a2a4a]"
                      >
                        <div className="aspect-video bg-black rounded-lg mb-3 overflow-hidden">
                          <video 
                            src={video.video_url} 
                            className="w-full h-full object-cover"
                            controls
                          />
                        </div>
                        <h4 className="text-white font-medium">{video.title}</h4>
                        <p className="text-gray-400 text-sm">{video.subtitle}</p>
                        <div className="flex gap-2 mt-3">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setEditingItem(video);
                              setVideoForm({
                                title: video.title,
                                title_no: video.title_no || "",
                                subtitle: video.subtitle || "",
                                subtitle_no: video.subtitle_no || "",
                                button_text: video.button_text || "",
                                button_text_no: video.button_text_no || "",
                                button_link: video.button_link || "",
                                color: video.color || "purple",
                                icon: video.icon || "Building2",
                                videoFile: null,
                                posterFile: null
                              });
                              setShowVideoModal(true);
                            }}
                            className="border-[#2a2a4a] text-gray-300"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Rediger
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => deleteVideo(video.video_id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* STATS TAB */}
          <TabsContent value="stats" className="space-y-6">
            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Statistikk</CardTitle>
                <Button 
                  onClick={() => { setStatForm({ value: "", label: "", label_no: "", icon: "TrendingUp" }); setEditingItem(null); setShowStatModal(true); }}
                  className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Legg til statistikk
                </Button>
              </CardHeader>
              <CardContent>
                {config?.stats?.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Ingen statistikk lagt til ennå</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {config?.stats?.map((stat) => (
                      <div 
                        key={stat.stat_id}
                        className="p-4 bg-[#0f0f1a] rounded-lg border border-[#2a2a4a] text-center relative group"
                      >
                        <p className="text-3xl font-bold text-[#23F7DD]">{stat.value}</p>
                        <p className="text-gray-400 text-sm">{stat.label}</p>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => {
                              setEditingItem(stat);
                              setStatForm({
                                value: stat.value,
                                label: stat.label,
                                label_no: stat.label_no || "",
                                icon: stat.icon || "TrendingUp"
                              });
                              setShowStatModal(true);
                            }}
                            className="text-gray-400 hover:text-white h-8 w-8 p-0"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => deleteStat(stat.stat_id)}
                            className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TESTIMONIALS TAB */}
          <TabsContent value="testimonials" className="space-y-6">
            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Anmeldelser</CardTitle>
                <Button 
                  onClick={() => { 
                    setTestimonialForm({ quote: "", quote_no: "", author_name: "", author_role: "", author_role_no: "", author_company: "", rating: 5 }); 
                    setEditingItem(null); 
                    setShowTestimonialModal(true); 
                  }}
                  className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Legg til anmeldelse
                </Button>
              </CardHeader>
              <CardContent>
                {config?.testimonials?.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <Quote className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Ingen anmeldelser lagt til ennå</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {config?.testimonials?.map((testimonial) => (
                      <div 
                        key={testimonial.testimonial_id}
                        className="p-4 bg-[#0f0f1a] rounded-lg border border-[#2a2a4a]"
                      >
                        <div className="flex gap-1 mb-2">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          ))}
                        </div>
                        <p className="text-gray-300 italic mb-3">"{testimonial.quote}"</p>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">{testimonial.author_name}</p>
                            <p className="text-gray-400 text-sm">{testimonial.author_role}, {testimonial.author_company}</p>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => {
                                setEditingItem(testimonial);
                                setTestimonialForm({
                                  quote: testimonial.quote,
                                  quote_no: testimonial.quote_no || "",
                                  author_name: testimonial.author_name,
                                  author_role: testimonial.author_role,
                                  author_role_no: testimonial.author_role_no || "",
                                  author_company: testimonial.author_company || "",
                                  rating: testimonial.rating
                                });
                                setShowTestimonialModal(true);
                              }}
                              className="text-gray-400 hover:text-white"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => deleteTestimonial(testimonial.testimonial_id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FOOTER TAB */}
          <TabsContent value="footer" className="space-y-6">
            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardHeader>
                <CardTitle className="text-white">Footer</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Firmanavn</Label>
                    <Input
                      value={footerForm.company_name}
                      onChange={(e) => setFooterForm({...footerForm, company_name: e.target.value})}
                      className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Org.nummer</Label>
                    <Input
                      value={footerForm.org_number}
                      onChange={(e) => setFooterForm({...footerForm, org_number: e.target.value})}
                      className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300">Adresse</Label>
                  <Input
                    value={footerForm.address}
                    onChange={(e) => setFooterForm({...footerForm, address: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">E-post</Label>
                    <Input
                      value={footerForm.email}
                      onChange={(e) => setFooterForm({...footerForm, email: e.target.value})}
                      className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Telefon</Label>
                    <Input
                      value={footerForm.phone}
                      onChange={(e) => setFooterForm({...footerForm, phone: e.target.value})}
                      className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                    />
                  </div>
                </div>
                <div className="border-t border-[#2a2a4a] pt-4">
                  <h4 className="text-white font-medium mb-3">Sosiale medier</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-300">Facebook</Label>
                      <Input
                        value={footerForm.facebook}
                        onChange={(e) => setFooterForm({...footerForm, facebook: e.target.value})}
                        className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                        placeholder="https://facebook.com/..."
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Instagram</Label>
                      <Input
                        value={footerForm.instagram}
                        onChange={(e) => setFooterForm({...footerForm, instagram: e.target.value})}
                        className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                        placeholder="https://instagram.com/..."
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">LinkedIn</Label>
                      <Input
                        value={footerForm.linkedin}
                        onChange={(e) => setFooterForm({...footerForm, linkedin: e.target.value})}
                        className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                        placeholder="https://linkedin.com/..."
                      />
                    </div>
                  </div>
                </div>
                <Button 
                  onClick={saveFooter} 
                  disabled={saving}
                  className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Lagrer..." : "Lagre endringer"}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* SERVICE MODAL */}
        <Dialog open={showServiceModal} onOpenChange={setShowServiceModal}>
          <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingItem ? "Rediger tjeneste" : "Legg til tjeneste"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Tittel (EN)</Label>
                  <Input
                    value={serviceForm.title}
                    onChange={(e) => setServiceForm({...serviceForm, title: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Tittel (NO)</Label>
                  <Input
                    value={serviceForm.title_no}
                    onChange={(e) => setServiceForm({...serviceForm, title_no: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Beskrivelse (EN)</Label>
                  <Textarea
                    value={serviceForm.description}
                    onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Beskrivelse (NO)</Label>
                  <Textarea
                    value={serviceForm.description_no}
                    onChange={(e) => setServiceForm({...serviceForm, description_no: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-300">Ikon</Label>
                  <Select value={serviceForm.icon} onValueChange={(v) => setServiceForm({...serviceForm, icon: v})}>
                    <SelectTrigger className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-[#2a2a4a]">
                      {ICON_OPTIONS.map(icon => (
                        <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Farge</Label>
                  <Select value={serviceForm.color} onValueChange={(v) => setServiceForm({...serviceForm, color: v})}>
                    <SelectTrigger className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-[#2a2a4a]">
                      {COLOR_OPTIONS.map(color => (
                        <SelectItem key={color.value} value={color.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded ${color.class}`}></div>
                            {color.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Link</Label>
                  <Input
                    value={serviceForm.link}
                    onChange={(e) => setServiceForm({...serviceForm, link: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                    placeholder="/client/stillastjenester"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={serviceForm.is_coming_soon}
                  onCheckedChange={(v) => setServiceForm({...serviceForm, is_coming_soon: v})}
                />
                <Label className="text-gray-300">Kommer snart</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowServiceModal(false)} className="text-gray-400">
                Avbryt
              </Button>
              <Button onClick={saveService} disabled={saving} className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black">
                {saving ? "Lagrer..." : "Lagre"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* VIDEO MODAL */}
        <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
          <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingItem ? "Rediger video" : "Last opp video"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Tittel (EN)</Label>
                  <Input
                    value={videoForm.title}
                    onChange={(e) => setVideoForm({...videoForm, title: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Tittel (NO)</Label>
                  <Input
                    value={videoForm.title_no}
                    onChange={(e) => setVideoForm({...videoForm, title_no: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Undertekst (EN)</Label>
                  <Input
                    value={videoForm.subtitle}
                    onChange={(e) => setVideoForm({...videoForm, subtitle: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Undertekst (NO)</Label>
                  <Input
                    value={videoForm.subtitle_no}
                    onChange={(e) => setVideoForm({...videoForm, subtitle_no: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-300">Knappetekst (EN)</Label>
                  <Input
                    value={videoForm.button_text}
                    onChange={(e) => setVideoForm({...videoForm, button_text: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Knappetekst (NO)</Label>
                  <Input
                    value={videoForm.button_text_no}
                    onChange={(e) => setVideoForm({...videoForm, button_text_no: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Knappelink</Label>
                  <Input
                    value={videoForm.button_link}
                    onChange={(e) => setVideoForm({...videoForm, button_link: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                    placeholder="/register"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Farge</Label>
                  <Select value={videoForm.color} onValueChange={(v) => setVideoForm({...videoForm, color: v})}>
                    <SelectTrigger className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-[#2a2a4a]">
                      {COLOR_OPTIONS.map(color => (
                        <SelectItem key={color.value} value={color.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded ${color.class}`}></div>
                            {color.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Ikon</Label>
                  <Select value={videoForm.icon} onValueChange={(v) => setVideoForm({...videoForm, icon: v})}>
                    <SelectTrigger className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-[#2a2a4a]">
                      {ICON_OPTIONS.map(icon => (
                        <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-[#0f0f1a] rounded-lg border border-dashed border-[#2a2a4a]">
                  <div className="flex items-center gap-2 mb-2">
                    <Video className="w-5 h-5 text-[#23F7DD]" />
                    <span className="text-white font-medium">Video *</span>
                  </div>
                  {videoForm.videoFile && (
                    <p className="text-[#23F7DD] text-sm mb-2">{videoForm.videoFile.name}</p>
                  )}
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(e) => setVideoForm({...videoForm, videoFile: e.target.files?.[0] || null})}
                    className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#23F7DD] file:text-black hover:file:bg-[#1de9cf]"
                  />
                </div>
                <div className="p-4 bg-[#0f0f1a] rounded-lg border border-dashed border-[#2a2a4a]">
                  <div className="flex items-center gap-2 mb-2">
                    <Image className="w-5 h-5 text-purple-400" />
                    <span className="text-white font-medium">Poster (valgfritt)</span>
                  </div>
                  {videoForm.posterFile && (
                    <p className="text-purple-400 text-sm mb-2">{videoForm.posterFile.name}</p>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setVideoForm({...videoForm, posterFile: e.target.files?.[0] || null})}
                    className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-500 file:text-white hover:file:bg-purple-600"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowVideoModal(false)} className="text-gray-400">
                Avbryt
              </Button>
              <Button onClick={saveVideo} disabled={saving} className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black">
                {saving ? "Laster opp..." : "Lagre"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* STAT MODAL */}
        <Dialog open={showStatModal} onOpenChange={setShowStatModal}>
          <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a]">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingItem ? "Rediger statistikk" : "Legg til statistikk"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300">Verdi (f.eks. "500+", "99%")</Label>
                <Input
                  value={statForm.value}
                  onChange={(e) => setStatForm({...statForm, value: e.target.value})}
                  className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  placeholder="500+"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Etikett (EN)</Label>
                  <Input
                    value={statForm.label}
                    onChange={(e) => setStatForm({...statForm, label: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                    placeholder="Projects Completed"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Etikett (NO)</Label>
                  <Input
                    value={statForm.label_no}
                    onChange={(e) => setStatForm({...statForm, label_no: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                    placeholder="Prosjekter Fullført"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Ikon</Label>
                <Select value={statForm.icon} onValueChange={(v) => setStatForm({...statForm, icon: v})}>
                  <SelectTrigger className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a2e] border-[#2a2a4a]">
                    {ICON_OPTIONS.map(icon => (
                      <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowStatModal(false)} className="text-gray-400">
                Avbryt
              </Button>
              <Button onClick={saveStat} disabled={saving} className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black">
                {saving ? "Lagrer..." : "Lagre"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* TESTIMONIAL MODAL */}
        <Dialog open={showTestimonialModal} onOpenChange={setShowTestimonialModal}>
          <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingItem ? "Rediger anmeldelse" : "Legg til anmeldelse"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Sitat (EN)</Label>
                  <Textarea
                    value={testimonialForm.quote}
                    onChange={(e) => setTestimonialForm({...testimonialForm, quote: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Sitat (NO)</Label>
                  <Textarea
                    value={testimonialForm.quote_no}
                    onChange={(e) => setTestimonialForm({...testimonialForm, quote_no: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Navn</Label>
                  <Input
                    value={testimonialForm.author_name}
                    onChange={(e) => setTestimonialForm({...testimonialForm, author_name: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Selskap</Label>
                  <Input
                    value={testimonialForm.author_company}
                    onChange={(e) => setTestimonialForm({...testimonialForm, author_company: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-300">Rolle (EN)</Label>
                  <Input
                    value={testimonialForm.author_role}
                    onChange={(e) => setTestimonialForm({...testimonialForm, author_role: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                    placeholder="CEO"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Rolle (NO)</Label>
                  <Input
                    value={testimonialForm.author_role_no}
                    onChange={(e) => setTestimonialForm({...testimonialForm, author_role_no: e.target.value})}
                    className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1"
                    placeholder="Daglig leder"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Vurdering</Label>
                  <Select value={String(testimonialForm.rating)} onValueChange={(v) => setTestimonialForm({...testimonialForm, rating: parseInt(v)})}>
                    <SelectTrigger className="bg-[#0f0f1a] border-[#2a2a4a] text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-[#2a2a4a]">
                      {[5, 4, 3, 2, 1].map(r => (
                        <SelectItem key={r} value={String(r)}>
                          {"⭐".repeat(r)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowTestimonialModal(false)} className="text-gray-400">
                Avbryt
              </Button>
              <Button onClick={saveTestimonial} disabled={saving} className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black">
                {saving ? "Lagrer..." : "Lagre"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
